import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { Map } from 'immutable';

import { Timestamp } from "../../../firebase";
import { Select as AntSelect, Form, message } from "antd";

import { DatePicker } from "../../atoms/DatePicker";
import { Select } from "../../atoms/Select";
import { ConfirmationModalBody } from "../../organisms/ConfirmationModalBody";
import { Table } from "../../organisms/Table";
import { Modal } from "../../organisms/Modal";

import {
  HeaderText,
  Wrapper,
  InputContainer,
  TableWrapper,
  NameWrapper,
  StarIcon,
} from "./elements";
import { data, format } from "../../../constants";
import { theme } from "../../../config";
import { onUpdateApplicantStatus } from "../../../utils/scripts/modal";
import { FilterFilled } from "@ant-design/icons";
import { statusFilter } from "../../../utils/helpers/filter";
import { star } from "../../../assets/icons/index";
import { arrayToObject } from "../../../utils/helpers/transform";
import { FailedApplicantModalBody } from "../../organisms/FailedApplicantModalBody";

const { Option } = AntSelect;

type Props = {
  tableData: any;
  onClickApplicant: (key: any) => void;
  isLoading: boolean;
  onUpdateApplicant: (id: string, params: any) => void;
  error?: string;
  isUpdating: boolean;
  onChange?: (pagination: any, filters: any) => void;
  total: number;
  currentPage?: number | undefined;
  updateTotalNewApplicants: (currentPhase: string, prevPhase: string, currentStatus: string) => void;
  fetchApplicants: (lastElementDocs?: unknown, limit?: number, subsStatus?: string, position?: string) => void;
  fetchTotalApplicants: () => void;
};

const TestdomeExam: FC<Props> = ({
  tableData,
  onClickApplicant,
  isLoading,
  onUpdateApplicant,
  error,
  isUpdating,
  total,
  updateTotalNewApplicants,
  fetchApplicants,
  fetchTotalApplicants,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [applicantName, setApplicantName] = useState<string>("");
  const [selectedRowApplicant, setSelectedRowApplicant] = useState<any>(null);
  const [selectedRowStatus, setSelectedRowStatus] = useState<string>("");
  const [filterDropdown, setFilterDropdownVisible] = useState<any>({
    isVisible: false,
    dropdownKey: "",
    rowKey: "",
  });

  const [applicantsData, setApplicantsData] = useState<any>({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastElement, setLastElement] = useState(undefined);

  const [form] = Form.useForm();
  const datePickerRef = useRef<any>([]);
  const statusDropdownRef = useRef<any>([]);

  const [modalType, setModalType] = useState<string>("");
  const [failedReason, setFailedReason] = useState<string>("");
  const [failedReasonsData, setfailedReasonsData] = useState<string[]>(
    data.failedReasons
  );
  const [customReason, setCustomReason] = useState<string>("");
  const [otherReasonErrorMessage, setOtherReasonErrorMessage] =
    useState<string>("");
  const [isToBeDeclined, setIsToBeDeclined] = useState<boolean>(false);

  const onScrollPage = useCallback(() => {
    setFilterDropdownVisible((freshState: any) => {
      if (datePickerRef.current[filterDropdown.rowKey]) {
        datePickerRef.current[filterDropdown.rowKey].blur();
      }
      if (statusDropdownRef.current[filterDropdown.rowKey]) {
        statusDropdownRef.current[filterDropdown.rowKey].blur();
      }
      return { ...freshState, isVisible: false };
    });
  }, [filterDropdown]);

  useEffect(() => {
    document.addEventListener("scroll", onScrollPage);
    return () => {
      document.removeEventListener("scroll", onScrollPage);
    };
  }, [filterDropdown, onScrollPage]);

  useEffect(() => {
    if (tableData && tableData?.response) {
      const lists = tableData.response.data;
      const whereQuery = tableData.response.query.where;
      const arrToObj = Map(arrayToObject(lists));
      if (whereQuery[0][2] === 'Technical Exam') {
        const filterApplicantsData: any = Object.values(applicantsData)
          .filter((values: any) =>
            values.applicationStatus.currentStatus === 'Technical Exam',
          );
        const mapApplicantsData = Map(arrayToObject(filterApplicantsData));
        const newShortlistedApplicantsList = mapApplicantsData.mergeDeep(arrToObj);
        setApplicantsData(newShortlistedApplicantsList.toJS());
        setLastElement(tableData.response.lastElement);
      }
    }
  }, [tableData]);

  const handleClickRow = (record: any) => {
    onClickApplicant(record.key);
  };

  const handleSave = async (
    row: { key: any; phase: any },
    hasFail?: boolean,
    isToFail?: boolean
  ) => {
    const statusFail = hasFail
      ? {
        "applicationStatus.currentStatus": "Failed",
        "applicationStatus.failedPhase": "Technical Exam",
        "applicationStatus.dateMoved": Timestamp.now(),
      }
      : {};

    if (hasFail) {
      await updateTotalNewApplicants("Failed", "Technical Exam", "");
    }

    if (!isToFail) {
      onUpdateApplicant(row.key, {
        "scheduleDate.technicalExam.startDate": row.phase.startDate
          ? new Date(row.phase.startDate)
          : null,
        "scheduleDate.technicalExam.endDate": row.phase.endDate
          ? new Date(row.phase.endDate)
          : null,
        "applicationStatus.subStatus": row.phase.status || null,
        ...statusFail,
      });
    } else {
      onUpdateApplicant(row.key, {
        "applicationStatus.reason": row.phase.reason || null,
        ...statusFail,
      });
    }

    setTimeout(() => {
      if (hasFail) {
        const { [row.key]: val, ...other } = applicantsData;
        setApplicantsData(other);
      } else {
        const newValues = {
          scheduleDate: {
            technicalExam: {
              startDate: row.phase.startDate
                ? moment(row.phase.startDate)
                : null,
              endDate: row.phase.endDate ? moment(row.phase.endDate) : null,
            },
          },
          applicationStatus: {
            ...applicantsData[row.key].applicationStatus,
            subStatus: row.phase.status,
          },
        };

        const newData = {
          ...applicantsData,
          [row.key]: {
            ...applicantsData[row.key],
            ...newValues,
          },
        };
        setApplicantsData(newData);
      }
    }, 500);
  };

  const save = async (record: any, data: any) => {
    try {
      handleSave({
        ...record,
        phase: { ...record.phase, ...data },
      });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  const _updateStatusFunc = (
    applicant: any,
    updatedStatus: string,
    hasFail?: boolean
  ) => {
    return handleSave(
      {
        ...applicant,
        phase: {
          ...applicant.phase,
          status: updatedStatus,
        },
      },
      hasFail
    );
  };

  const _updateFailedStatusFunc = async (
    applicant: any,
    failedReason: string,
    hasFail?: boolean
  ) => {
    return handleSave(
      {
        ...applicant,
        phase: {
          ...applicant.phase,
          interviewDate: applicant.phase.interviewDate,
          reason: failedReason,
        },
      },
      hasFail,
      true
    );
  };

  const onUpdateStatus = (applicant: any, updatedStatus: string) => {
    setSelectedRowApplicant(applicant);
    setSelectedRowStatus(updatedStatus);
    if (!navigator.onLine) {
      form.resetFields();
      message.config({ duration: 1.5, maxCount: 1 });
      return message.error(data.toastNetworkErrorMessage);
    }
    if (updatedStatus.toLowerCase() === "failed") {
      setModalType("failedApplicant");
      setShowModal(true);
    } else if (updatedStatus.toLowerCase() === "declined") {
      setIsToBeDeclined(true);
      setShowModal(true);
    } else {
      onUpdateApplicantStatus(
        applicant,
        updatedStatus,
        setApplicantName,
        setShowModal,
        _updateStatusFunc
      );
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      width: "320px",
      fixed: "left",
      render: (text: string, row: any) => {
        return row.referredBy ? (
          <NameWrapper>
            <span>{text}</span>
            <StarIcon src={star} />
          </NameWrapper>
        ) : (
          text
        );
      },
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      width: "250px",
      filterIcon: (
        <FilterFilled
          className={
            filterDropdown.dropdownKey === "position" &&
              filterDropdown.isVisible
              ? "tableFilterDropdownPositionActive"
              : ""
          }
        />
      ),
      filterDropdownVisible:
        filterDropdown.isVisible && filterDropdown.dropdownKey === "position",
      onFilterDropdownVisibleChange: (visible: any) => {
        setFilterDropdownVisible({
          isVisible: visible,
          dropdownKey: "position",
        });
      },
      filters: data.positionApplied.map((position: string) => {
        return { text: position, value: position };
      }),
      onFilter: (value: any, record: any) =>
        record.position.indexOf(value) === 0,
    },
    {
      title: "Start Date",
      dataIndex: ["phase", "startDate"],
      key: "startDate",
      width: "200px",
      render: (text: string, row: any) => {
        return (
          <InputContainer onClick={(e) => e.stopPropagation()}>
            <Form.Item>
              <DatePicker
                key={row.key}
                onChange={(date: any) => {
                  save(row, {
                    startDate: moment(date).format(format.dateFormat),
                  });
                }}
                value={
                  text
                    ? moment(new Date(text), format.dateTimeFormat)
                    : undefined
                }
                disabledDate={(current) => {
                  if (row.phase.endDate) {
                    return (
                      moment(row.phase.endDate).format("MM/DD/YYY").valueOf() <
                      current.format("MM/DD/YYY").valueOf()
                    );
                  }
                  return false;
                }}
                ref={(el) => {
                  datePickerRef.current[`start-${row.key}`] = el;
                }}
                onOpenChange={(open) =>
                  setFilterDropdownVisible({
                    isVisible: open,
                    dropdownKey: "testDateDropdown",
                    rowKey: `start-${row.key}`,
                  })
                }
              />
            </Form.Item>
          </InputContainer>
        );
      },
    },
    {
      title: "End Date",
      dataIndex: ["phase", "endDate"],
      key: "endDate",
      width: "200px",
      render: (text: string, row: any) => {
        return (
          <InputContainer onClick={(e) => e.stopPropagation()}>
            <Form.Item>
              <DatePicker
                disabledDate={(current) => {
                  return (
                    moment(row.phase.startDate).format("MM/DD/YYY").valueOf() >
                    current.format("MM/DD/YYY").valueOf()
                  );
                }}
                key={row.key}
                disabled={
                  row.phase &&
                  !row.phase.startDate &&
                  row.phase.startDate === null
                }
                onChange={(date: any) => {
                  save(row, {
                    endDate: moment(date).format(format.dateFormat),
                  });
                }}
                value={
                  text
                    ? moment(new Date(text), format.dateTimeFormat)
                    : undefined
                }
                ref={(el) => {
                  datePickerRef.current[`end-${row.key}`] = el;
                }}
                onOpenChange={(open) =>
                  setFilterDropdownVisible({
                    isVisible: open,
                    dropdownKey: "testDateDropdown",
                    rowKey: `end-${row.key}`,
                  })
                }
              />
            </Form.Item>
          </InputContainer>
        );
      },
    },
    {
      title: "Status",
      dataIndex: ["phase", "status"],
      key: "status",
      width: "200px",
      filterIcon: (
        <FilterFilled
          className={
            filterDropdown.dropdownKey === "status" && filterDropdown.isVisible
              ? "tableFilterDropdownStatusActive"
              : ""
          }
        />
      ),
      filterDropdownVisible:
        filterDropdown.isVisible && filterDropdown.dropdownKey === "status",
      onFilterDropdownVisibleChange: (visible: any) => {
        setFilterDropdownVisible({ isVisible: visible, dropdownKey: "status" });
      },
      filters: statusFilter(),
      onFilter: (value: any, record: any) =>
        record.phase.status.indexOf(value) === 0,
      render: (text: string, row: any) => {
        return (
          <InputContainer onClick={(e) => e.stopPropagation()}>
            <Form form={form}>
              <Form.Item shouldUpdate name="status">
                <Select
                  defaultValue={text === "Unassigned" ? "" : text}
                  placeholder="Select"
                  optionItems={
                    <>
                      {data.applicationStatus.map((status: string) => (
                        <Option key={status} value={status}>
                          {status}
                        </Option>
                      ))}
                    </>
                  }
                  handleChange={(status: string) => onUpdateStatus(row, status)}
                  ref={(el) => (statusDropdownRef.current[row.key] = el)}
                  $onDropdownVisibleChange={(open) =>
                    setFilterDropdownVisible({
                      isVisible: open,
                      dropdownKey: "testStatusDropdown",
                      rowKey: row.key,
                    })
                  }
                />
              </Form.Item>
            </Form>
          </InputContainer>
        );
      },
    },
    {
      title: "Date Moved",
      dataIndex: "dateMoved",
      key: "dateMoved",
      width: "200px",
      render: (text: string) => {
        return <>{moment(new Date(text)).format(format.dateTimeFormat)}</>;
      },
    },
  ];

  const onConfirmModal = () => {
    if (selectedRowStatus.toLowerCase() === "declined") {
      _updateStatusFunc(selectedRowApplicant, selectedRowStatus);
      setShowModal(false);
      setIsToBeDeclined(false);
    } else {
      _updateStatusFunc(selectedRowApplicant, selectedRowStatus, true);
      setShowModal(false);
    }
  };

  const onConfirmFailModal = () => {
    _updateFailedStatusFunc(selectedRowApplicant, failedReason, true);
    setShowModal(false);
    setFailedReason("");
  };

  const onCancelModal = () => {
    form.resetFields();
    setShowModal(false);
    setIsToBeDeclined(false);
  };

  const addOtherReason = (e: any, customReason: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (customReason.trim() !== "" && customReason.trim().length <= 50) {
      setfailedReasonsData((prevItems) => [...prevItems, customReason]);
      setCustomReason("");
    }
    if (customReason.trim() === "") {
      setOtherReasonErrorMessage("Must not be empty");
    }
    if (customReason.trim().length >= 50) {
      setOtherReasonErrorMessage(
        "This field accepts 50 number of characters only."
      );
    }
  };

  const modalBody =
    modalType === "failedApplicant" ? (
      <FailedApplicantModalBody
        applicantName={selectedRowApplicant?.fullName}
        currentStatus={selectedRowApplicant?.phase.status}
        isInitialsReasonSelected={data.failedReasons.includes(failedReason)}
        handleSelectChange={(reason: string) => {
          setFailedReason(reason);
        }}
        phaseKey="Failed"
        placeholder="Select"
        failedReasonsData={failedReasonsData}
        customReason={customReason}
        handleInputChange={(e: any) => {
          setCustomReason(e.target.value);
          e.target.value.length > 50
            ? setOtherReasonErrorMessage(
              "This field accepts 50 number of characters only."
            )
            : setOtherReasonErrorMessage("");
        }}
        handlePressEnter={(e: any) => addOtherReason(e, customReason)}
        otherReasonErrorMessage={otherReasonErrorMessage}
        failedReason={failedReason}
        onModalConfirm={onConfirmFailModal}
        onModalCancel={() => {
          onCancelModal();
          setFailedReason("");
        }}
      />
    ) : (
      <ConfirmationModalBody
        applicantName={applicantName}
        fromStatus={
          isToBeDeclined
            ? selectedRowApplicant?.phase.status
            : selectedRowStatus
        }
        toStatus={isToBeDeclined ? "Declined" : "Failed"}
        toStatusColor={theme.colors.utilities.error}
        onConfirm={onConfirmModal}
        onCancel={onCancelModal}
      />
    );

  const handleOnChange = (pagination: any, filters: any) => {
    const { current: page, pageSize: currPageSize } = pagination;
    const filteredPosition = filters.position || undefined;
    const filteredStatus = filters.status || undefined;
    fetchTotalApplicants();
    if (filters.position || filters.status) {
      setCurrentPage(1);
    }

    setPageSize(currPageSize);
    if (pageSize === currPageSize) {
      if (page > 1) {
        if (currentPage + 1 >= page) {
          fetchApplicants(lastElement, currPageSize, filteredStatus, filteredPosition);
        } else {
          fetchApplicants(undefined, page * currPageSize, filteredStatus, filteredPosition);
        }
      } else {
        fetchApplicants(undefined, currPageSize, filteredStatus, filteredPosition);
      }
      setCurrentPage(page);
    } else {
      setCurrentPage(1);
      fetchApplicants(undefined, page * currPageSize, filteredStatus, filteredPosition);
    }
  };

  const mappedApplicantsData =
    Object.values(applicantsData).map((applicant: any, index: number) => {
      const status =
        !applicant.applicationStatus?.subStatus ||
          applicant.applicationStatus?.subStatus === "Read" ||
          applicant.applicationStatus?.subStatus === "Unread"
          ? ""
          : applicant.applicationStatus?.subStatus;
      const scheduleDate = applicant.scheduleDate;
      const applicantName = applicant.applicantName
        ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
        : applicant.fullName;
      return {
        key: applicant.id,
        fullName: applicantName,
        position: applicant.positionApplied,
        dateMoved: applicant.applicationStatus.dateMoved
          ? `${moment(
            applicant.applicationStatus.dateMoved.toDate()
          ).format("MM/DD/YYYY hh:mm a")}`
          : null,
        phase: {
          startDate: scheduleDate?.technicalExam?.startDate
            ? `${moment(
              scheduleDate?.technicalExam?.startDate.toDate()
            ).format("MM/DD/YYYY hh:mm a")}`
            : null,
          endDate: scheduleDate?.technicalExam?.endDate
            ? `${moment(
              scheduleDate?.technicalExam?.endDate.toDate()
            ).format("MM/DD/YYYY hh:mm a")}`
            : null,
          status: status || "Unassigned",
        },
        referredBy: applicant.referredBy,
      };
    }).sort(
      (a: any, b: any) => {
        const date1: any = new Date(a.dateMoved);
        const date2: any = new Date(b.dateMoved);
        return (date2 - date1);
      },
    );

  return (
    <Wrapper onScroll={onScrollPage}>
      <HeaderText>Technical Exam</HeaderText>
      <TableWrapper>
        <Table
          isLoading={isLoading || isUpdating}
          tableData={mappedApplicantsData}
          columns={columns as any}
          rowRecord={handleClickRow}
          onChange={handleOnChange}
          totalItems={total}
          current={currentPage}
        />
      </TableWrapper>
      <Modal
        visible={showModal}
        onModalClose={onCancelModal}
        title={"Confirmation to move applicant status"}
        body={modalBody}
      />
    </Wrapper>
  );
};

export default TestdomeExam;
